import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import api from '../api';
import { buildStudentHoursParts } from '../printStudentHours';
import {
  EXCLUDE_SUBCODES,
  FINAL_SUBCODE,
  subcodeOf,
  hv,
  sumHours,
  pctText,
  buildExamRows,
} from './ExamSummary';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

// 교육이수증명서 원본 크기(A4, 96dpi). 화면에서는 이 비율로 높이에 맞춰 줄여서 보여준다.
const CERT_W = 595 * (96 / 72); // 793.3px
const CERT_H = 841 * (96 / 72); // 1121.3px

const MIN_ROW = 9.5; // 줄 높이가 이보다 작아지면 한 화면에 맞추지 못하고 스크롤을 허용한다
const MAX_ROW = 28;

const ASSET_BASE = `${process.env.PUBLIC_URL}/docs`;

// 증명서 양식에 들어가는 값. 지금은 성명만 선택한 학생에 따라 바뀌고, 나머지는 샘플 값이다.
// (학생의 영문 이름, 생년월일, 주소, 교육기간 등이 학생 정보에 생기면 이 값들을 학생별로 채운다)
const CERT_SAMPLE = {
  number: '제2026-A001호',
  birth: '2008.07.30.',
  address: '경상북도 영주시 풍기읍 금계로 90',
  atoNo: '2014-ATO-M-005-04',
  theoryPeriod: '2024.03.05. ~ 2026.10.29.(총 1,330시간)',
  practicePeriod: '2024.03.05. ~ 2026.10.29.(총 1,100시간)',
  entryDate: '2024년 3월 5일',
  issueDate: '2026년 10월 29일',
  director: '문 명 성',
};

const FONT_GOTHIC = "'Malgun Gothic', '맑은 고딕', sans-serif";
const FONT_GULIM = "'Gulim', '굴림', 'Malgun Gothic', sans-serif";
const FONT_BATANG = "'HCR Batang', 'Batang', '바탕', 'Times New Roman', serif";

// 증명서 글자 줄 맞춤: data-w(pt) 로 지정된 줄을 원본 PDF 의 줄 폭에 맞게 단어/글자 간격으로 조정한다.
// (원본은 한글 문서에서 줄마다 자간/어간이 조절되어 있다.) 화면과 인쇄 창에서 같은 함수를 쓴다.
// 인쇄 창에는 이 함수의 소스를 그대로 넣으므로 바깥 변수를 쓰지 않는다.
function fitCertLines(root) {
  root.querySelectorAll('[data-w]').forEach((el) => {
    el.style.letterSpacing = '0px';
    el.style.wordSpacing = '0px';
    const target = (parseFloat(el.getAttribute('data-w')) * 96) / 72;
    const natural = el.offsetWidth;
    const diff = target - natural;
    if (Math.abs(diff) < 0.3) return;
    const text = el.textContent;
    const spaces = (text.match(/ /g) || []).length;
    if (diff > 0 && spaces > 0) el.style.wordSpacing = `${diff / spaces}px`;
    else if (text.length > 1) el.style.letterSpacing = `${diff / (text.length - 1)}px`;
  });
}

// 교육이수증명서 양식(A4). 글자 위치/크기/줄 폭은 샘플 PDF(교육이수증명서.pdf)에서 그대로 옮겼다 (단위: pt).
// x: 첫 글자 왼쪽, y: 글자 기준선, s: 글자 크기, w: 줄 폭(생략하면 글꼴 기본 폭)
// 스타일을 모두 인라인으로 두어, 화면과 인쇄 창에서 같은 모양으로 나온다.
// assetBase: 로고/직인 이미지가 있는 폴더 주소 (인쇄 창에서는 절대 주소가 필요하다)
function CertificatePaper({ name, fields = CERT_SAMPLE, assetBase = ASSET_BASE }) {
  const rootRef = useRef(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    fitCertLines(root);
    let alive = true;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => alive && fitCertLines(root));
    }
    return () => {
      alive = false;
    };
  }, [name, fields]);

  const f = fields;
  const lines = [
    { k: 'no', x: 100.7, y: 112.9, s: 12.95, w: 160.2, t: `번호(No.) : ${f.number}` },
    { k: 'title', x: 229.1, y: 155.7, s: 20.02, w: 140.0, t: '이 수 증 명 서', font: FONT_GULIM },
    { k: 'title-en', x: 129.4, y: 187.6, s: 20.02, w: 339.3, t: '(Certificate of Course Completion)', font: FONT_BATANG, bold: true },
    { k: 'name-label', x: 100.7, y: 234.5, s: 12.95, t: '성' },
    { k: 'name', x: 137.1, y: 234.5, s: 12.95, t: `명(Name): ${name}` }, // 학생에 따라 바뀌는 줄이라 폭을 고정하지 않는다
    { k: 'birth', x: 100.7, y: 255.2, s: 12.95, w: 204.9, t: `생년월일(Date of Birth): ${f.birth}` },
    { k: 'addr-label', x: 100.7, y: 275.9, s: 12.95, t: '주' },
    { k: 'addr', x: 138.0, y: 275.9, s: 12.95, w: 285.0, t: `소(Address): ${f.address}` },
    { k: 'p1', x: 112.3, y: 317.5, s: 12.95, w: 366.0, t: `위 사람은 전문교육기관 (지정서 번호 : ${f.atoNo})의`, bold: true },
    { k: 'p2', x: 92.8, y: 338.3, s: 12.95, w: 412.2, t: '항공정비사 (비행기) 과정을 다음과 같이 정상 수료하였음을 증명합니다.', bold: true },
    { k: 'p3', x: 112.3, y: 359.1, s: 12.95, w: 393.5, t: 'This is to certify that the above person has successfully', bold: true },
    { k: 'p4', x: 92.8, y: 379.9, s: 12.95, w: 412.9, t: 'completed the airplane maintenance training course of Approved', bold: true },
    { k: 'p5', x: 92.8, y: 400.6, s: 12.95, w: 324.5, t: `Training Organization (ATO No : ${f.atoNo})`, bold: true },
    { k: 'b1', x: 102.5, y: 442.2, s: 9.5, t: '○' },
    { k: 'period', x: 119.8, y: 442.2, s: 12.95, w: 151.7, t: '교육기간(Training Period)' },
    { k: 'theory', x: 107.2, y: 462.9, s: 12.95, w: 395.3, t: `- 학과교육(Theoretical Training) : ${f.theoryPeriod}` },
    { k: 'practice', x: 107.2, y: 483.8, s: 12.95, w: 394.8, t: `- 실기교육(Practical Training) : ${f.practicePeriod}` },
    { k: 'b2', x: 102.5, y: 525.3, s: 9.5, t: '○' },
    { k: 'entry', x: 119.8, y: 525.3, s: 12.95, w: 313.4, t: `항공정비사(비행기) 과정 입과년도 : ${f.entryDate}` },
    { k: 'issue', x: 240.2, y: 567.7, s: 14.03, w: 117.8, t: f.issueDate, bold: true },
    { k: 'issue-en', x: 251.0, y: 590.1, s: 14.03, w: 96.6, t: '(Date of issue)', bold: true },
    { k: 'director', x: 120.8, y: 673.7, s: 21.94, w: 318.8, t: `경북항공기술교육원장 ${f.director}`, bold: true },
  ];

  return (
    <div
      ref={rootRef}
      style={{
        position: 'relative',
        width: '595pt',
        height: '841pt',
        background: '#fff',
        color: '#000',
        overflow: 'hidden',
      }}
    >
      <img
        src={`${assetBase}/cert-emblem.png`}
        alt=""
        style={{ position: 'absolute', left: '169.3pt', top: '340.4pt', width: '259.6pt', height: '118.7pt' }}
      />
      <div
        style={{ position: 'absolute', left: '129.4pt', top: '190.6pt', width: '339.3pt', height: '0.84pt', background: '#000' }}
      />
      {lines.map((l) => (
        <div
          key={l.k}
          data-w={l.w}
          style={{
            position: 'absolute',
            left: `${l.x}pt`,
            top: `${l.y - l.s * 0.91}pt`,
            fontSize: `${l.s}pt`,
            lineHeight: 1,
            whiteSpace: 'pre',
            fontFamily: l.font || FONT_GOTHIC,
            fontWeight: l.bold ? 700 : 400,
          }}
        >
          {l.t}
        </div>
      ))}
      <img
        src={`${assetBase}/cert-seal.png`}
        alt=""
        style={{ position: 'absolute', left: '439.2pt', top: '655pt', width: '70.9pt', height: '70.8pt' }}
      />
    </div>
  );
}

function escapeHtml(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// 교육이수증명서(A4 한 장)를 인쇄용 창으로 연다. includeDetails 이면 다음 장부터 지정과목이수현황과 평가결과를 붙인다.
// 열린 창에서 [PDF 저장 / 인쇄]를 누르고 대상(프린터)을 "PDF로 저장"으로 고르면 PDF 파일로 저장된다.
function openCompletionPrint({ student, year, info, hourGroups, totals, examTable, includeDetails }) {
  const certHtml = renderToStaticMarkup(
    <CertificatePaper name={student.name} assetBase={`${window.location.origin}${ASSET_BASE}`} />
  );
  let hoursCss = '';
  let hoursBody = '';
  if (includeDetails) {
    const parts = buildStudentHoursParts(info, hourGroups, totals, examTable, { heading: '지정과목이수현황 및 평가결과' });
    hoursCss = parts.css;
    hoursBody = parts.body;
  }
  const title = `${year}학년도 ${escapeHtml(student.name)} ${includeDetails ? '교육이수증명서 및 이수현황' : '교육이수증명서'}`;
  const html = `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8" />
<title>${title}</title>
<style>
  * { box-sizing: border-box; }
  @page { size: A4 portrait; margin: 15mm 12mm; }
  html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: '맑은 고딕', 'Malgun Gothic', sans-serif; color: #111; margin: 0; padding: 24px; }
  .print-bar { text-align: right; margin-bottom: 12px; }
  .print-bar button { padding: 6px 16px; font-size: 14px; cursor: pointer; }
  ${hoursCss}
  @page cert { size: A4 portrait; margin: 0; }
  .cert-page { page: cert; width: 595pt; height: 841pt; margin: 0 auto 24px; overflow: hidden; background: #fff; ${includeDetails ? 'page-break-after: always;' : ''} }
  @media print {
    body { padding: 0; }
    .print-bar { display: none; }
    .cert-page { margin: 0; }
  }
</style>
</head>
<body>
  <div class="print-bar">
    <button onclick="window.print()">PDF 저장 / 인쇄</button>
  </div>
  <div class="cert-page">${certHtml}</div>${hoursBody}
  <script>
    (function () {
      var fit = ${fitCertLines.toString()};
      var run = function () { fit(document); };
      run();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);
      window.addEventListener('load', run);
      window.addEventListener('beforeprint', run);
    })();
  </script>
</body>
</html>`;

  const popup = window.open('', '_blank', 'width=900,height=1000');
  if (!popup) {
    alert('팝업이 차단되었습니다. 브라우저의 팝업 차단을 해제해 주세요.');
    return;
  }
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  popup.focus();
}

// 교육이수증명: 학생을 고르면 교육이수증명서와 과목이수현황, 평가결과를 스크롤 없이 한 화면에 보여준다.
export default function CompletionCertificate() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [classtype, setClasstype] = useState('M');
  const [grade, setGrade] = useState('');
  const [classOptions, setClassOptions] = useState([]);
  const [classNo, setClassNo] = useState('');
  const [studid, setStudid] = useState('');
  const [report, setReport] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const bodyRef = useRef(null);
  const filtersRef = useRef(null);
  // fh: 조회조건 줄이 차지하는 높이(여백 포함). 증명서는 이 줄과 나란히 맨 위부터 시작한다.
  const [box, setBox] = useState({ w: 1200, h: 640, fh: 70 });

  useEffect(() => {
    if (!pdfOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setPdfOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pdfOpen]);

  useEffect(() => {
    api.get('/exam-subjects').then((res) => setSubjects(res.data));
  }, []);

  useEffect(() => {
    setClassNo('');
    if (!grade) {
      setClassOptions([]);
      return undefined;
    }
    let cancelled = false;
    api.get('/classes', { params: { grade, classtype } }).then((res) => {
      if (!cancelled) setClassOptions(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [grade, classtype]);

  useEffect(() => {
    setStudid('');
    setReport(null);
    setRecords([]);
    if (!grade || !classNo) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get('/admin/student-hours-report', { params: { year, grade, classtype, class_no: Number(classNo) } }),
      api.get('/exam-results', { params: { year, classtype, grade, class_no: Number(classNo) } }),
    ])
      .then(([hoursRes, examRes]) => {
        if (cancelled) return;
        setReport(hoursRes.data);
        setRecords(examRes.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, grade, classtype, classNo]);

  // 화면에 남은 높이/너비를 재서 본문 영역의 크기로 삼는다
  useLayoutEffect(() => {
    const measure = () => {
      const el = bodyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      const fh = filtersRef.current ? filtersRef.current.offsetHeight + 8 : 70;
      setBox({
        w: Math.floor(rect.width),
        h: Math.max(420, Math.floor(window.innerHeight - top - 12)),
        fh,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const students = report?.students ?? [];
  const student = students.find((s) => s.studid === studid) || null;
  const classLabel = `${classtype === 'M' ? '면장반' : '정규반'} ${classNo}반`;

  const hourGroups = useMemo(() => {
    if (!student) return [];
    const groups = [];
    (report?.chapters ?? []).forEach((c) => {
      let g = groups[groups.length - 1];
      if (!g || g.subject !== c.group_subject) {
        g = { subject: c.group_subject, chapters: [], legalThry: 0, legalPrac: 0 };
        groups.push(g);
      }
      g.chapters.push(c);
      g.legalThry += c.legal_thrytime || 0;
      g.legalPrac += c.legal_practime || 0;
    });
    // 전자·전기·계기 과목은 항공기체 바로 다음에 보여준다 (두 단의 줄 수가 비슷해져 아래 선이 맞는다)
    const eIdx = groups.findIndex((g) => g.subject.startsWith('전자'));
    const bIdx = groups.findIndex((g) => g.subject === '항공기체');
    if (eIdx >= 0 && bIdx >= 0 && eIdx !== bIdx + 1) {
      const [electro] = groups.splice(eIdx, 1);
      groups.splice(groups.findIndex((g) => g.subject === '항공기체') + 1, 0, electro);
    }
    return groups.map((g) => {
      const sub = sumHours(student, g.chapters);
      return {
        subject: g.subject,
        legalThry: g.legalThry,
        legalPrac: g.legalPrac,
        thry: sub.thry,
        prac: sub.prac,
        rows: g.chapters.map((c) => {
          const h = student.hours[c.amtcode] || { thrytime: 0, practime: 0 };
          return {
            chapter: c.amt_chapter,
            legalThry: c.legal_thrytime || 0,
            legalPrac: c.legal_practime || 0,
            thry: h.thrytime || 0,
            prac: h.practime || 0,
          };
        }),
      };
    });
  }, [student, report]);

  const totals = {
    legalThry: report?.legal_total_thrytime ?? 0,
    legalPrac: report?.legal_total_practime ?? 0,
    thry: student?.total_thrytime || 0,
    prac: student?.total_practime || 0,
  };

  const examTable = useMemo(() => {
    if (!student) return [];
    const mine = records.filter((r) => r.studid === student.studid);
    return subjects
      .filter((s) => !EXCLUDE_SUBCODES.includes(subcodeOf(s.amtcode)))
      .map((s) => {
        const isFinal = subcodeOf(s.amtcode) === FINAL_SUBCODE;
        return { subject: s.subject, isFinal, rows: buildExamRows(mine, s.amtcode, isFinal ? 1 : 2) };
      })
      .sort((a, b) => Number(a.isFinal) - Number(b.isFinal));
  }, [student, records, subjects]);

  // 과목이수현황을 두 단으로 나눈다. 왼쪽 단은 이수현황만, 오른쪽 단은 이수현황 + 시수계 + 평가결과이므로
  // 두 단의 줄 수 차이가 가장 작아지는 과목 경계를 고른다.
  const layout = useMemo(() => {
    const HEAD = 2; // 표 머리글 2줄
    const examRows = examTable.reduce((n, g) => n + g.rows.length, 0) + 2;
    const rightExtra = 1 /* 시수계 */ + 1.6 /* 평가결과 제목 */ + examRows + HEAD;
    let best = { split: 1, cost: Infinity, left: 0, right: 0 };
    for (let k = 1; k < hourGroups.length; k += 1) {
      const left = HEAD + hourGroups.slice(0, k).reduce((n, g) => n + g.rows.length + 1, 0);
      const right = HEAD + hourGroups.slice(k).reduce((n, g) => n + g.rows.length + 1, 0) + rightExtra - HEAD;
      const cost = Math.max(left, right);
      if (cost < best.cost) best = { split: k, cost, left, right };
    }
    return best;
  }, [hourGroups, examTable]);

  const titleH = 28; // 표 위 제목줄 높이(px)
  const colH = box.h - titleH - box.fh;
  // 줄이 더 많은 단 기준으로 줄 높이를 정하고, 줄이 적은 단은 그보다 조금 키워서 두 단의 바닥을 맞춘다
  const fitRow = (rows) => Math.floor((colH / (rows + 0.4)) * 10) / 10;
  const rawRow = layout.cost > 0 ? fitRow(layout.cost) : MAX_ROW;
  const rowH = Math.max(MIN_ROW, Math.min(MAX_ROW, rawRow));
  const rowLeft = rowH;
  const rowRight = rowH;
  const fontPx = Math.max(8, Math.min(13, Math.round(rowH * 0.62 * 10) / 10));
  const fits = rawRow >= MIN_ROW;

  // 교육이수증명서는 높이에 맞추되 너비가 본문의 42%를 넘지 않게 한다
  const certScale = Math.min(box.h / CERT_H, (box.w * 0.42) / CERT_W);
  const certW = Math.floor(CERT_W * certScale);

  const leftGroups = hourGroups.slice(0, layout.split);
  const rightGroups = hourGroups.slice(layout.split);

  const handlePdf = (includeDetails) => {
    setPdfOpen(false);
    if (!student) return;
    openCompletionPrint({
      student,
      year,
      info: { year, grade, classLabel, student: { studid: student.studid, name: student.name } },
      hourGroups,
      totals,
      examTable,
      includeDetails,
    });
  };

  const hoursTable = (groups, withTotal) => (
    <table className="cc-table">
      <colgroup>
        <col style={{ width: '17%' }} />
        <col />
        <col style={{ width: '9%' }} />
        <col style={{ width: '9%' }} />
        <col style={{ width: '9%' }} />
        <col style={{ width: '9%' }} />
        <col style={{ width: '11%' }} />
      </colgroup>
      <thead>
        <tr>
          <th rowSpan={2}>과목</th>
          <th rowSpan={2}>교육내용</th>
          <th colSpan={2}>법정시수</th>
          <th colSpan={2}>이수시수</th>
          <th rowSpan={2}>진도율</th>
        </tr>
        <tr>
          <th>학과</th>
          <th>실습</th>
          <th>학과</th>
          <th>실습</th>
        </tr>
      </thead>
      <tbody>
        {groups.map((g) => (
          <React.Fragment key={g.subject}>
            {g.rows.map((r, i) => (
              <tr key={r.chapter + i}>
                {i === 0 && <td className="cc-subject" rowSpan={g.rows.length + 1}>{g.subject}</td>}
                <td className="cc-left" title={r.chapter}>{r.chapter}</td>
                <td>{hv(r.legalThry)}</td>
                <td>{hv(r.legalPrac)}</td>
                <td>{hv(r.thry)}</td>
                <td>{hv(r.prac)}</td>
                <td>{pctText(r.thry + r.prac, r.legalThry + r.legalPrac)}</td>
              </tr>
            ))}
            <tr className="cc-subtotal">
              <td className="cc-left">소계</td>
              <td>{hv(g.legalThry)}</td>
              <td>{hv(g.legalPrac)}</td>
              <td>{hv(g.thry)}</td>
              <td>{hv(g.prac)}</td>
              <td>{pctText(g.thry + g.prac, g.legalThry + g.legalPrac)}</td>
            </tr>
          </React.Fragment>
        ))}
        {withTotal && (
          <tr className="cc-total">
            <td colSpan={2}>시수계</td>
            <td>{hv(totals.legalThry)}</td>
            <td>{hv(totals.legalPrac)}</td>
            <td>{hv(totals.thry)}</td>
            <td>{hv(totals.prac)}</td>
            <td>{pctText(totals.thry + totals.prac, totals.legalThry + totals.legalPrac)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="cc-page">
      <div
        className="cc-body"
        ref={bodyRef}
        style={{
          height: box.h,
          '--cc-row': `${rowH}px`,
          '--cc-font': `${fontPx}px`,
          overflow: fits ? 'hidden' : 'auto',
        }}
      >
        {student && (
          <div className="cc-cert-wrap" style={{ width: certW, height: Math.floor(CERT_H * certScale) }}>
            <div
              style={{ width: CERT_W, height: CERT_H, transformOrigin: 'top left', transform: `scale(${certScale})` }}
            >
              <CertificatePaper name={student.name} />
            </div>
          </div>
        )}

        <div className="cc-right">
          <div className="cc-filters" ref={filtersRef}>
            <div className="form-field">
              <label>연도</label>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}년</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>구분</label>
              <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
                <option value="R">정규반</option>
                <option value="M">면장반</option>
              </select>
            </div>
            <div className="form-field">
              <label>학년</label>
              <select value={grade} onChange={(e) => setGrade(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value=""></option>
                <option value={1}>1학년</option>
                <option value={2}>2학년</option>
                <option value={3}>3학년</option>
              </select>
            </div>
            <div className="form-field">
              <label>반</label>
              <select value={classNo} onChange={(e) => setClassNo(e.target.value)}>
                <option value=""></option>
                {classOptions.map((c) => (
                  <option key={c} value={c}>{c}반</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>학생</label>
              <select value={studid} onChange={(e) => setStudid(e.target.value)}>
                <option value="">선택</option>
                {students.map((s) => (
                  <option key={s.studid} value={s.studid}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-field cc-pdf-field">
              <button
                type="button"
                className="btn-ghost cc-pdf-btn"
                onClick={() => setPdfOpen(true)}
                disabled={!student}
                title={student ? '교육이수증명서를 PDF로 저장합니다' : '학생을 선택하면 사용할 수 있습니다'}
              >
                PDF 다운로드
              </button>
            </div>
          </div>

          {!student && (
            <div className="cc-empty">
              {loading ? '불러오는 중...' : '학년, 반, 학생을 선택하면 이수증명서와 과목이수현황, 평가결과가 한 화면에 나타납니다.'}
            </div>
          )}

          {student && (
            <>
              <div className="cc-titlebar" style={{ height: titleH }}>
                <span />
                <h3>지정과목이수현황</h3>
                <div className="cc-student-info">
                  <span>{year}년</span>
                  <span>{classLabel}</span>
                  <span>{grade}학년</span>
                  <span>학번 {student.studid}</span>
                  <strong>{student.name}</strong>
                </div>
              </div>

              <div className="cc-cols">
                <div className="cc-col" style={{ '--cc-row': `${rowLeft}px` }}>{hoursTable(leftGroups, false)}</div>
                <div className="cc-col cc-col-right" style={{ '--cc-row': `${rowRight}px` }}>
                  {hoursTable(rightGroups, true)}

                  <div className="cc-exam-block">
                  <h3 className="cc-exam-title">평가결과</h3>
                  <table className="cc-table cc-exam-table">
                    <colgroup>
                      <col style={{ width: '24%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '13%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '13%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th rowSpan={2}>과목</th>
                        <th rowSpan={2}>회수</th>
                        <th colSpan={2}>학과</th>
                        <th colSpan={2}>실기</th>
                      </tr>
                      <tr>
                        <th>일자</th>
                        <th>점수</th>
                        <th>일자</th>
                        <th>점수</th>
                      </tr>
                    </thead>
                    <tbody>
                      {examTable.map((g) =>
                        g.rows.map((r, i) => (
                          <tr key={g.subject + i}>
                            {i === 0 && (
                              <td className="cc-subject" rowSpan={g.rows.length} colSpan={g.isFinal ? 2 : 1}>
                                {g.subject}
                              </td>
                            )}
                            {!g.isFinal && <td>{r.no}</td>}
                            <td>{r.tDate}</td>
                            <td>{r.tScore}</td>
                            <td>{r.pDate}</td>
                            <td>{r.pScore}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {pdfOpen && student && (
        <div className="cc-modal-backdrop" onClick={() => setPdfOpen(false)}>
          <div className="cc-modal" role="dialog" aria-modal="true" aria-label="PDF 다운로드" onClick={(e) => e.stopPropagation()}>
            <h3>PDF 다운로드</h3>
            <p>{student.name} 학생의 PDF에 어떤 내용을 담을까요?</p>
            <button type="button" className="cc-modal-option" onClick={() => handlePdf(false)}>
              <strong>교육이수증명서만</strong>
              <span>교육이수증명서 1장 (PDF 저장 창이 열립니다)</span>
            </button>
            <button type="button" className="cc-modal-option" onClick={() => handlePdf(true)}>
              <strong>과목이수현황과 평가결과 포함</strong>
              <span>교육이수증명서 + 지정과목이수현황 + 평가결과 (PDF 저장 창이 열립니다)</span>
            </button>
            <div className="cc-modal-actions">
              <button type="button" className="btn-ghost" onClick={() => setPdfOpen(false)}>취소</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
